/**
 * Community auto-fill — port of the monolith's __commAddon + the name→code
 * resolver behind auto-fill and the dup-job community fallback
 * (legacy/smart-email-request/index.html L10810–10920, L5395–5424).
 *
 * commAddon() builds the live-content band (hero + floorplan cards + available
 * homes table) that gets spliced before </body> of the selected template's
 * real HTML; communityFill() does the whole pull-and-splice, returning the
 * monolith contract: { ok, community, plans, homesites, html }.
 */
/* Through the page-level promise cache, not `./api` directly (2026-08-20). The
   preview rail's `useEchDyn` resolves and packs the SAME community the moment it is
   picked on Step 1, and auto-fill then asks for it again ~600ms later — measured in
   Chrome as two identical `/sfmc-content?community=26275` requests. Same route,
   same answer, one request. See echFeeds.ts. */
import { feedCommunityPack as getCommunityPack, feedCommunitySearch as searchCommunities } from "./echFeeds";
import { planImg } from "./dynamicFill";
import type { CommunityPackResponse, CommunityInfo } from "../types/api";

const esc = (s: unknown) => String(s == null ? "" : s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/"/g, "&quot;");
const money = (n?: number) => (n ? "$" + Number(n).toLocaleString() : "");

/** monolith cmCore — strip a community name down to its searchable core */
export function cmCore(name: string): string {
  return String(name).replace(/\([^)]*\)/g, "").replace(/\s*-\s*/g, " ")
    .replace(/[0-9]+['’]?s?\b/g, " ")
    .replace(/\b(Collection|Classic|Series|Townhomes?|Villas?|Estates?|Manor|The)\b/gi, " ")
    .replace(/\s+/g, " ").trim().split(/\s+/).slice(0, 3).join(" ");
}

/** monolith __lvResolveName-shape: community NAME → SFMC {code,name} (exact
 *  lower-case match wins, else the first hit; the stripped core is the retry). */
export async function resolveCommunityName(raw: string): Promise<{ code: string; name: string } | null> {
  const seed = raw.replace(/\([^)]*\)/g, "").replace(/\s*-\s*/g, " ").trim();
  const core = cmCore(raw);
  let items = (await searchCommunities(seed).catch(() => null))?.items || [];
  if (!items.length && core && core !== seed) {
    items = (await searchCommunities(core).catch(() => null))?.items || [];
  }
  if (!items.length) return null;
  const rl = raw.toLowerCase();
  const best = items.find((c) => (c.name || "").toLowerCase() === rl) || items[0];
  return best.code ? { code: best.code, name: best.name || raw } : null;
}

export interface CommAddon { addon: string; name: string; plans: number; homes: number }

/** monolith __commAddon — hero band + floorplan cards + available-homes table */
export function commAddon(C: CommunityPackResponse): CommAddon {
  const cm: Partial<CommunityInfo> = C.community || {};
  const heroBand = cm.hero
    ? '<table role="presentation" width="600" align="center" cellpadding="0" cellspacing="0" style="width:600px;margin:0 auto"><tr><td><img src="' + esc(cm.hero) + '" width="600" style="display:block;width:100%;max-width:600px" alt="' + esc(cm.name) + '"></td></tr><tr><td style="background:#1B365D;padding:18px 24px;font-family:Arial"><div style="color:#fff;font-size:22px;font-weight:800">' + esc(cm.name) + '</div><a href="' + esc(cm.tour || cm.url || "#") + '" style="color:#C8A664;font-weight:700;text-decoration:none;font-size:14px">Schedule a Tour &rarr;</a></td></tr></table>'
    : "";
  const plans = (C.plans || []).slice(0, 6);
  const cards = plans.map((p) => {
    const im = planImg(p);
    return '<td width="50%" valign="top" style="padding:8px"><table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #e5e0d6;border-radius:8px;overflow:hidden;font-family:Arial"><tr><td>' + (im ? '<img src="' + esc(im) + '" width="100%" style="display:block;width:100%" alt="' + esc(p.name) + '">' : "") + '</td></tr><tr><td style="padding:12px"><div style="font-size:16px;font-weight:800;color:#1B365D">' + esc(p.name) + '</div><div style="font-size:12px;color:#5b6472;margin:4px 0">' + esc(p.beds) + " bd &middot; " + esc(p.baths) + " ba &middot; " + esc(p.sqft) + ' sq ft</div>' + (p.price ? '<div style="font-size:14px;font-weight:800;color:#1B365D">From ' + money(p.price) + "</div>" : "") + '<a href="' + esc(p.url || "#") + '" style="display:inline-block;margin-top:8px;color:#C8A664;font-weight:700;text-decoration:none;font-size:13px">View Plan &rarr;</a></td></tr></table></td>';
  });
  let pr = "";
  for (let i = 0; i < cards.length; i += 2) pr += "<tr>" + cards[i] + (cards[i + 1] || '<td width="50%"></td>') + "</tr>";
  const plansSection = plans.length
    ? '<table role="presentation" width="600" align="center" cellpadding="0" cellspacing="0" style="width:600px;margin:0 auto;font-family:Arial"><tr><td style="padding:22px 8px 4px;font-size:19px;font-weight:800;color:#1B365D">Floorplans at ' + esc(cm.name) + '</td></tr></table><table role="presentation" width="600" align="center" cellpadding="0" cellspacing="0" style="width:600px;margin:0 auto">' + pr + "</table>"
    : "";
  const homes = (C.homesites || []).slice(0, 8);
  const hr = homes.map((h) =>
    '<tr><td style="padding:9px 12px;border-top:1px solid #f0ece3;font-family:Arial;font-size:13px"><b>' + esc(h.addr || h.plan || "Home") + '</b><div style="color:#8792a3">' + esc(h.beds) + " bd &middot; " + esc(h.baths) + " ba &middot; " + esc(h.sqft) + " sq ft &middot; " + esc(h.status || "") + '</div></td><td style="padding:9px 12px;border-top:1px solid #f0ece3;text-align:right;font-family:Arial;font-weight:800;color:#1B365D;white-space:nowrap">' + money(h.price) + "</td></tr>");
  const homesSection = homes.length
    ? '<table role="presentation" width="600" align="center" cellpadding="0" cellspacing="0" style="width:600px;margin:0 auto;font-family:Arial;border-collapse:collapse"><tr><td colspan="2" style="padding:20px 12px 4px;font-size:19px;font-weight:800;color:#1B365D">Available Homes' + (cm.name ? " at " + esc(cm.name) : "") + "</td></tr>" + hr.join("") + "</table>"
    : "";
  return {
    addon: '<div style="background:#f5f0e8;padding:14px 0">' + heroBand + plansSection + homesSection + "</div>",
    name: cm.name || "", plans: plans.length, homes: homes.length,
  };
}

export interface CommunityFillResult {
  ok: boolean;
  err?: string;
  community?: string;
  plans?: number;
  homesites?: number;
  html?: string;
  /** min plan price — the monolith's __commContent.fromPrice (feeds the AI copy
   *  engine's "From $X · live pricing" chip + price-anchored subjects) */
  fromPrice?: number;
}

/** monolith communityFill/communityFillMulti (single-community path): pull the
 *  content pack, build the addon, splice it before </body> of the template. */
export async function communityFill(code: string, tplHtml: string): Promise<CommunityFillResult> {
  try {
    const C = await getCommunityPack(code).catch(() => null);
    if (!C || !C.ok) return { ok: false, err: "no live content for " + code };
    const a = commAddon(C);
    const out = tplHtml.indexOf("</body>") > -1 ? tplHtml.replace("</body>", a.addon + "</body>") : tplHtml + a.addon;
    /* monolith __commContent: fromPrice = min of the pack's plan prices */
    const pr = (C.plans || []).map((p) => p.price).filter((n): n is number => typeof n === "number" && n > 0);
    return { ok: true, community: a.name, plans: a.plans, homesites: a.homes, html: out, fromPrice: pr.length ? Math.min(...pr) : 0 };
  } catch (e) {
    return { ok: false, err: String(e) };
  }
}
